/**
 * 67.20 (c): what a unit's team knows of the enemy on the action clock — the one accessor every action brain reads.
 *
 *   own look      — the enemies the unit saw at its last look (its brain's `seenAt`: the ground under each, as of now)
 *   picture       — with the team picture on: the team's shared tracks (every teammate's sightings, `told`), the team's
 *                   instruments in `state.sightings` (a drone's or a probe's identified sighting, `probe`; a tower's radar
 *                   dot, `sensor`, no identity) and, always, the team's marks (a commander's fix, `mark`)
 *
 * Each entry carries its confidence (its source's half-life, core/awareness; a mark's own fade) and the tick it was
 * made. Never the state's truth about an enemy nobody saw (the fairness rule). Pure: state in, a list out.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { PHYS_DT } from '../constants';
import { personPointKept, type GroundPointKept } from '../core/fineTerrain';
import { personSetOf } from '../core/scaleProfile';
import { confidenceAfterSeconds } from '../core/awareness';
import { markTasksFor, markPos, markConfidence } from '../core/marks';
import { brainFace } from '../core/brainFace';
import { trackConfidenceAt, SIGHT_PERIOD_TICKS } from '../core/picture';

const seenKept = new WeakMap<object, GroundPointKept>();
/**
 * 62.65 (d), the team's knowledge once a tick (ARCHITECTURE §5.16 "the brains' costs by layer" (1)): every belief's entry
 * — a track of the team's picture, an instrument's sighting, a mark, the unit's own look — is turned into its `KnownEnemy`
 * once per tick per entry object and kept on it (a WeakMap: the entry is copy-on-write, a new sighting a new object), so
 * the feed's reads (up to sixteen a bot a tick) assemble shared objects instead of computing a confidence (a dexp) and
 * allocating one per belief per read. The same numbers whoever asks first; the filters (`after`, `minConfidence`) read the
 * kept values as they read the fresh ones. The objects are shared: callers read them, never write into them.
 */
const trackKept = new WeakMap<object, { now: number; config: unknown; known: KnownEnemy }>();
const sightKept = new WeakMap<object, { now: number; known: KnownEnemy }>();
const markKept = new WeakMap<object, { now: number; issuedTick: number; pos: Vec3; known: KnownEnemy }>();
const lookKept = new WeakMap<object, { now: number; pos: Vec3; known: KnownEnemy }>();

export type KnownSource = 'seen' | 'told' | 'probe' | 'sensor' | 'mark';

export interface KnownEnemy {
  /** Where it was believed to be (an eye point, world space). */
  pos: Vec3;
  /** When the belief was made (action tick). */
  tick: number;
  /** 0–1 at `now`. */
  confidence: number;
  source: KnownSource;
  /** The unit, when the source identified it (absent for a radar dot or a mark on ground). */
  unitId?: number;
  /** Its planet, when the source says (a shared track does not; it is wherever its teammate saw it). */
  planetIndex?: number;
}

export interface TeamKnowledgeOptions {
  /** The team picture is on for this team (ActionKnowledge.teamPicture): tracks and instruments are known. */
  picture: boolean;
  /** The team's tracks as of this tick (the brain's in-tick working copy; default `state.teamTracks[team]`). */
  tracks?: ReadonlyMap<number, { pos: Vec3; tick: number }> | Iterable<[number, { pos: Vec3; tick: number }]>;
  /** Only beliefs made after this tick (a place already searched is not news). Default: all. */
  after?: number;
  /** Below this confidence a belief is dropped. Default 0. */
  minConfidence?: number;
  /** Include the unit's own last look. Default true. */
  ownLook?: boolean;
}

/**
 * Everything the unit's team knows of the enemy at tick `now`, in a fixed order: own look, the picture's tracks (by unit
 * id), the team's instruments on the unit's planet (sightings in state order), the team's marks on the unit's planet.
 */
export function teamKnowledge(state: GameState, unit: PlayerState, now: number, opts: TeamKnowledgeOptions): KnownEnemy[] {
  const after = opts.after ?? -Infinity, min = opts.minConfidence ?? 0;
  const out: KnownEnemy[] = [];
  if (opts.ownLook !== false) {
    // its last look (at most a sight period old): where each enemy stood, on the ground
    for (const s of brainFace(state, unit.id)?.seenAt ?? []) {
      const planet = state.planets[s.planetIndex];
      if (!planet) continue;
      // on the person's ground (the terrain, its hummocks); 62.65 (d): placed once per entry while the ground stands (a look
      // makes new entries; the feed is read up to sixteen times a bot a tick)
      const pos = personPointKept(seenKept, s, planet, s.surfaceDir, personSetOf(state), 0, 'at');
      const k = lookKept.get(s);
      if (k && k.now === now && k.pos === pos) { out.push(k.known); continue; }
      const known: KnownEnemy = { pos, tick: now, confidence: 1, source: 'seen', unitId: s.id, planetIndex: s.planetIndex };
      lookKept.set(s, { now, pos, known });
      out.push(known);
    }
  }
  if (opts.picture) {
    const tracks = opts.tracks ?? state.teamTracks?.[unit.teamIndex] ?? [];
    for (const [id, t] of tracks instanceof Map ? tracks.entries() : tracks) {
      if (t.tick <= after) continue;
      const k = trackKept.get(t);
      if (k && k.now === now && k.config === state.config) { if (k.known.confidence >= min) out.push(k.known); continue; }
      // (182 (1), pictureDecay: the team picture's own confidence — eyes on within a sight period, then the same `told` curve)
      const { source, confidence: c } = state.config.pictureDecay === true ? trackConfidenceAt(t.tick, now, SIGHT_PERIOD_TICKS) : { source: 'told' as const, confidence: confidenceAfterSeconds('told', (now - t.tick) * PHYS_DT) };
      const known: KnownEnemy = { pos: t.pos, tick: t.tick, confidence: c, source, unitId: id };
      trackKept.set(t, { now, config: state.config, known });
      if (c < min) continue;
      out.push(known);
    }
    for (const x of state.sightings ?? []) {
      if (x.teamIndex !== unit.teamIndex || x.planetIndex !== unit.planetIndex || x.tick <= after) continue;
      const k = sightKept.get(x);
      if (k && k.now === now) { if (k.known.confidence >= min) out.push(k.known); continue; }
      const source = x.by === 'tower' ? 'sensor' : 'probe';
      const c = confidenceAfterSeconds(source, (now - x.tick) * PHYS_DT);
      const known: KnownEnemy = { pos: x.pos, tick: x.tick, confidence: c, source, ...(x.unitId !== undefined ? { unitId: x.unitId } : {}), planetIndex: x.planetIndex };
      sightKept.set(x, { now, known });
      if (c < min) continue;
      out.push(known);
    }
  }
  for (const m of markTasksFor(state, unit.teamIndex)) {
    if (m.order.planetIndex !== unit.planetIndex || m.issuedTick <= after) continue;
    const pos = markPos(state, m.order);
    if (!pos) continue;
    const k = markKept.get(m.order);
    if (k && k.now === now && k.issuedTick === m.issuedTick && k.pos === pos) { if (k.known.confidence >= min) out.push(k.known); continue; }
    const c = markConfidence(m.issuedTick, now);
    const known: KnownEnemy = { pos, tick: m.issuedTick, confidence: c, source: 'mark', ...(m.order.unitId !== undefined ? { unitId: m.order.unitId } : {}), planetIndex: m.order.planetIndex };
    markKept.set(m.order, { now, issuedTick: m.issuedTick, pos, known });
    if (c < min) continue;
    out.push(known);
  }
  return out;
}

/** The most confident belief (the first of equals, in teamKnowledge's order), or null. */
export function mostConfident(known: readonly KnownEnemy[]): KnownEnemy | null {
  let best: KnownEnemy | null = null;
  for (const k of known) if (!best || k.confidence > best.confidence) best = k;
  return best;
}

/** One belief per identified enemy (its most confident; the first of equals), then the unidentified ones as they came. */
export function knownEnemies(known: readonly KnownEnemy[]): KnownEnemy[] {
  const byUnit = new Map<number, KnownEnemy>();
  const rest: KnownEnemy[] = [];
  for (const k of known) {
    if (k.unitId === undefined) { rest.push(k); continue; }
    const had = byUnit.get(k.unitId);
    if (!had || k.confidence > had.confidence) byUnit.set(k.unitId, k);
  }
  return [...[...byUnit.values()].sort((a, b) => a.unitId! - b.unitId!), ...rest];
}
